// Eigenes Kontextmenü (Rechtsklick, lange Berührung, Shift+F10 / Kontextmenü-Taste) – reine Logik ohne DOM:
// Einträge, Bereinigung nach Rechten, Position im Fenster, Tastatur-Navigation und die Menüs der Fachbereiche.
// Die Oberfläche dazu steht in ui/Kontextmenue.tsx.

export interface Eintrag {
  text: string;
  icon?: string;
  /** Tastenkürzel als Hinweis, z. B. „Strg+C“ */
  kuerzel?: string;
  aktion?: () => unknown;
  deaktiviert?: boolean;
  /** rot (Löschen, Entfernen); die Aktion fragt selbst nach */
  gefahr?: boolean;
  /** false = nicht anzeigen (fehlendes Recht, passt nicht zum Ziel) */
  wenn?: boolean;
  /** aktueller Wert (z. B. gesetzter Status) – mit Häkchen */
  gewaehlt?: boolean;
  /** Untermenü: ersetzt beim Öffnen die Einträge (mit „Zurück“) */
  unter?: Zeile[];
  testid?: string;
}
/** Eintrag oder Trennlinie ('-'). */
export type Zeile = Eintrag | '-';

/** Einträge ohne Recht entfernen, leere Untermenüs weglassen, Trennlinien nur zwischen Gruppen. */
export function bereinigen(zeilen: (Zeile | null | undefined | false)[]): Zeile[] {
  const out: Zeile[] = [];
  for (const z of zeilen) {
    if (!z) continue;
    if (z === '-') {
      if (out.length && out[out.length - 1] !== '-') out.push('-');
      continue;
    }
    if (z.wenn === false) continue;
    if (z.unter) {
      const unter = bereinigen(z.unter);
      if (!unter.some((x) => x !== '-')) continue;
      out.push({ ...z, unter });
    } else out.push(z);
  }
  while (out[out.length - 1] === '-') out.pop();
  return out;
}

/** Wählbar per Tastatur und Maus? */
export const waehlbar = (z: Zeile | undefined): z is Eintrag => !!z && z !== '-' && !z.deaktiviert;

/** Nächster wählbarer Eintrag in Richtung +1/-1 (rundum); -1, wenn es keinen gibt. */
export function naechster(zeilen: Zeile[], aktiv: number, richtung: 1 | -1): number {
  const n = zeilen.length;
  for (let i = 1; i <= n; i++) {
    const k = (((aktiv + richtung * i) % n) + n) % n;
    if (waehlbar(zeilen[k])) return k;
  }
  return -1;
}
export const erster = (zeilen: Zeile[]) => naechster(zeilen, -1, 1);
export const letzter = (zeilen: Zeile[]) => naechster(zeilen, zeilen.length, -1);

/**
 * Position des Menüs: rechts unterhalb des Zeigers; passt es dort nicht hin, links bzw. oberhalb davon;
 * in jedem Fall ganz im Fenster (Rand `rand`). Höher als das Fenster: oben beginnen und scrollen (maxHoehe).
 */
export function position(x: number, y: number, breite: number, hoehe: number, fensterBreite: number, fensterHoehe: number, rand = 6) {
  const maxHoehe = Math.max(40, fensterHoehe - 2 * rand);
  const h = Math.min(hoehe, maxHoehe);
  const w = Math.min(breite, Math.max(40, fensterBreite - 2 * rand));
  let left = x + w + rand > fensterBreite ? x - w : x;
  let top = y + h + rand > fensterHoehe ? y - h : y;
  left = Math.max(rand, Math.min(left, fensterBreite - w - rand));
  top = Math.max(rand, Math.min(top, fensterHoehe - h - rand));
  return { left: Math.round(left), top: Math.round(top), maxHoehe };
}

/** Testkennung aus dem Text: „Löschen“ → km-loeschen. */
export const kennung = (text: string) =>
  'km-' + text.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** Tastenkürzel für die Anzeige (Mac: ⌘, sonst Strg+). */
export const taste = (buchstabe: string, mac: boolean) => (mac ? '⌘' + buchstabe : 'Strg+' + buchstabe);

/** ISO-Datum plus n Tage. */
export function tagePlus(iso: string, n: number) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------- allgemeine Menüs

/** Textfeld (input, textarea, contenteditable). */
export function textfeldMenue(z: { auswahl: boolean; schreibbar: boolean; leer: boolean; geheim?: boolean; mac: boolean },
  a: { ausschneiden: () => unknown; kopieren: () => unknown; einfuegen: () => unknown; allesMarkieren: () => unknown }): Zeile[] {
  return bereinigen([
    { text: 'Ausschneiden', icon: 'cut', kuerzel: taste('X', z.mac), aktion: a.ausschneiden, deaktiviert: !z.auswahl || !z.schreibbar || !!z.geheim },
    { text: 'Kopieren', icon: 'copy', kuerzel: taste('C', z.mac), aktion: a.kopieren, deaktiviert: !z.auswahl || !!z.geheim },
    { text: 'Einfügen', icon: 'paste', kuerzel: taste('V', z.mac), aktion: a.einfuegen, deaktiviert: !z.schreibbar },
    '-',
    { text: 'Alles markieren', icon: 'select', kuerzel: taste('A', z.mac), aktion: a.allesMarkieren, deaktiviert: z.leer },
  ]);
}

/** Link (<a href>). */
export function linkMenue(a: { neuerTab: () => unknown; kopieren: () => unknown }): Zeile[] {
  return [
    { text: 'In neuem Tab öffnen', icon: 'extern', aktion: a.neuerTab },
    { text: 'Link kopieren', icon: 'link', aktion: a.kopieren },
  ];
}

/** Überall sonst: Navigation des Browsers, Link der Seite, Suche. */
export function allgemeinesMenue(z: { zurueck: boolean; vorwaerts: boolean; suche: boolean; mac: boolean },
  a: { zurueck: () => unknown; vorwaerts: () => unknown; neuLaden: () => unknown; linkKopieren: () => unknown; suchen: () => unknown }): Zeile[] {
  return bereinigen([
    { text: 'Zurück', icon: 'back', aktion: a.zurueck, deaktiviert: !z.zurueck },
    { text: 'Vorwärts', icon: 'chevron', aktion: a.vorwaerts, deaktiviert: !z.vorwaerts },
    { text: 'Neu laden', icon: 'reload', kuerzel: z.mac ? '⌘R' : 'F5', aktion: a.neuLaden },
    '-',
    { text: 'Link dieser Seite kopieren', icon: 'link', aktion: a.linkKopieren },
    { text: 'Suchen', icon: 'search', aktion: a.suchen, wenn: z.suche },
  ]);
}

// ---------------------------------------------------------------- Diagramm

export interface StatusWert { id: string; name: string }

/** Schritt im Diagramm (auch Entscheidung, Verzweigung, Unterprozess-Kasten). */
export function schrittMenue(
  n: { typ: string; status?: string | null },
  r: { edit: boolean; darfPflegen: boolean; darfMitwirken: boolean; gespeichert: boolean },
  a: {
    oeffnen: () => unknown; unterprozess?: (() => unknown) | null; bearbeiten: () => unknown; status: (id: string | null) => unknown;
    aufgabe: () => unknown; dokument: () => unknown; linkKopieren: () => unknown; nachfolger: () => unknown; loeschen: () => unknown;
  },
  statusListe: StatusWert[],
): Zeile[] {
  const startEnde = n.typ === 'start' || n.typ === 'ende';
  const ungespeichert = r.gespeichert ? undefined : true;
  return bereinigen([
    { text: 'Details anzeigen', icon: 'info', aktion: a.oeffnen, testid: 'km-oeffnen' },
    { text: 'Unterprozess öffnen', icon: 'layers', aktion: a.unterprozess ?? undefined, wenn: !!a.unterprozess },
    { text: r.edit ? 'Umbenennen / bearbeiten' : 'Bearbeiten', icon: 'edit', aktion: a.bearbeiten, wenn: r.darfPflegen, testid: 'km-bearbeiten' },
    {
      text: 'Status setzen', icon: 'target', wenn: r.darfPflegen && !startEnde, testid: 'km-status',
      unter: [
        { text: 'Kein Status', aktion: () => a.status(null), gewaehlt: !n.status, testid: 'km-status-keiner' },
        ...statusListe.map((s) => ({ text: s.name, aktion: () => a.status(s.id), gewaehlt: n.status === s.id, testid: 'km-status-' + s.id })),
      ],
    },
    '-',
    { text: 'Aufgabe anlegen', icon: 'check', aktion: a.aufgabe, wenn: r.darfMitwirken, deaktiviert: ungespeichert },
    { text: 'Dokument anhängen', icon: 'upload', aktion: a.dokument, wenn: r.darfMitwirken, deaktiviert: ungespeichert },
    { text: 'Link kopieren', icon: 'link', aktion: a.linkKopieren },
    '-',
    { text: 'Nachfolger anlegen', icon: 'plus', aktion: a.nachfolger, wenn: r.edit && n.typ !== 'ende' },
    { text: 'Löschen', icon: 'trash', gefahr: true, aktion: a.loeschen, wenn: r.edit },
  ]);
}

// ---------------------------------------------------------------- Aufgaben

/** Aufgabe in Liste, Board, Gantt, Detail (Unteraufgaben), Bezugsblock und „Meine Aufgaben“. */
export function aufgabeMenue(
  t: { erledigt: boolean; zustaendig?: string | null; faellig?: string | null; beginn?: string | null; bezug?: boolean; gemerkt?: boolean },
  r: { darfSchreiben: boolean; darfLoeschen: boolean; ichEmail?: string },
  a: {
    oeffnen: () => unknown; neuerTab?: () => unknown; abhaken?: () => unknown; merken?: () => unknown; zuweisen?: (email: string) => unknown;
    faellig?: (iso: string | null) => unknown; unteraufgabe?: () => unknown; zuBezug?: () => unknown; linkKopieren: () => unknown; loeschen?: () => unknown;
  },
  heute: string,
): Zeile[] {
  const termin = (text: string, iso: string | null, testid: string): Eintrag => ({
    text, aktion: () => a.faellig!(iso), gewaehlt: (t.faellig ?? null) === iso, testid,
    // Fälligkeit nicht vor den Beginn legen (das lehnt auch der Server ab)
    deaktiviert: !!iso && !!t.beginn && t.beginn > iso,
  });
  const ich = (r.ichEmail ?? '').toLowerCase();
  return bereinigen([
    { text: 'Öffnen', icon: 'eye', aktion: a.oeffnen, testid: 'km-oeffnen' },
    { text: 'In neuem Tab öffnen', icon: 'extern', aktion: a.neuerTab, wenn: !!a.neuerTab },
    { text: t.erledigt ? 'Wieder öffnen' : 'Als erledigt markieren', icon: t.erledigt ? 'undo' : 'haken', aktion: a.abhaken, wenn: r.darfSchreiben && !!a.abhaken, testid: 'km-erledigt' },
    { text: t.gemerkt ? 'Nicht mehr merken' : 'Für mich merken', icon: 'stern', aktion: a.merken, wenn: !!a.merken, testid: 'km-merken' },
    '-',
    { text: 'Mich als verantwortlich eintragen', icon: 'users', aktion: () => a.zuweisen!(ich), wenn: r.darfSchreiben && !!a.zuweisen && !!ich && (t.zustaendig ?? '').toLowerCase() !== ich },
    {
      text: 'Fällig', icon: 'kalender', wenn: r.darfSchreiben && !!a.faellig, testid: 'km-faellig',
      unter: [
        termin('Heute', heute, 'km-faellig-heute'),
        termin('Morgen', tagePlus(heute, 1), 'km-faellig-morgen'),
        termin('In einer Woche', tagePlus(heute, 7), 'km-faellig-woche'),
        termin('Kein Datum', null, 'km-faellig-ohne'),
      ],
    },
    { text: 'Unteraufgabe hinzufügen', icon: 'plus', aktion: a.unteraufgabe, wenn: r.darfSchreiben && !!a.unteraufgabe },
    { text: 'Zum Bezug', icon: 'target', aktion: a.zuBezug, wenn: !!t.bezug && !!a.zuBezug },
    '-',
    { text: 'Link kopieren', icon: 'link', aktion: a.linkKopieren },
    '-',
    { text: 'Löschen', icon: 'trash', gefahr: true, aktion: a.loeschen, wenn: r.darfLoeschen && !!a.loeschen },
  ]);
}

// ---------------------------------------------------------------- Startseite

/** Projektkachel auf der Startseite. */
export function projektkachelMenue(
  p: { archiviert: boolean },
  r: { istAdmin: boolean; superadmin: boolean },
  a: { oeffnen: () => unknown; neuerTab: () => unknown; linkKopieren: () => unknown; archivieren: () => unknown; wiederherstellen: () => unknown; sicherung: () => unknown; loeschen: () => unknown },
): Zeile[] {
  return bereinigen([
    { text: 'Öffnen', icon: 'eye', aktion: a.oeffnen, testid: 'km-oeffnen' },
    { text: 'In neuem Tab öffnen', icon: 'extern', aktion: a.neuerTab },
    { text: 'Link kopieren', icon: 'link', aktion: a.linkKopieren },
    '-',
    { text: 'Archivieren …', icon: 'folder', aktion: a.archivieren, wenn: r.istAdmin && !p.archiviert, testid: 'km-archivieren' },
    { text: 'Wiederherstellen', icon: 'undo', aktion: a.wiederherstellen, wenn: r.istAdmin && p.archiviert, testid: 'km-wiederherstellen' },
    { text: 'Auf Sicherung zurücksetzen …', icon: 'undo', aktion: a.sicherung, wenn: r.superadmin, testid: 'km-sicherung' },
    '-',
    { text: 'Löschen …', icon: 'trash', gefahr: true, aktion: a.loeschen, wenn: r.superadmin, testid: 'km-loeschen' },
  ]);
}
